import { CloseOutlined, FileDoneOutlined } from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography } from "antd"
import React, { Suspense, lazy } from "react"
import { useTranslation } from "react-i18next"

import { useMinimizedWizard } from "../hooks/use-minimized-wizard.ts"
import {
  type WorkspaceSession,
  closeWorkspace,
  restoreWorkspace,
  useWorkspaceSession
} from "./workspace-store.ts"

const { Text } = Typography

const EsignWorkspace = lazy(() =>
  import("./EsignWorkspace.tsx").then((module) => ({
    default: module.EsignWorkspace
  }))
)

/** The workspace put aside: its document and step, one press from coming back. */
function MinimizedWorkspaceCard({ session }: { session: WorkspaceSession }) {
  const { t } = useTranslation()
  // The send wizard's own card sits in the same corner; this one stands above it.
  const stacked = Boolean(useMinimizedWizard())
  return (
    <section
      aria-label={t("esign.workspace.minimizedLabel")}
      data-testid="esign-workspace-card"
      className={`fixed right-24 z-[1000] w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-solid border-border bg-surface px-3 py-2 shadow-card max-md:right-4 ${
        stacked ? "bottom-20" : "bottom-4"
      }`}
    >
      <Flex align="center" gap="small">
        <FileDoneOutlined aria-hidden className="text-primary" />
        <button
          type="button"
          onClick={restoreWorkspace}
          className="flex min-w-0 flex-1 cursor-pointer flex-col items-start border-0 bg-transparent p-0 text-left"
        >
          <Text strong ellipsis className="max-w-full">
            {session.title || t("esign.workspace.title")}
          </Text>
          <Text type="secondary" className="text-xs">
            {session.view === "sign" && session.fields
              ? t("esign.workspace.minimizedFields", session.fields)
              : t("esign.workspace.minimizedRequest")}
          </Text>
        </button>
        <Button
          size="small"
          aria-label={t("esign.workspace.restore")}
          onClick={restoreWorkspace}
        >
          {t("esign.workspace.restore")}
        </Button>
        <Tooltip title={t("esign.workspace.close")}>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label={t("esign.workspace.close")}
            onClick={() => void closeWorkspace()}
          />
        </Tooltip>
      </Flex>
    </section>
  )
}

/**
 * Mounted once in the signed-in shell, so the workspace sits over whatever page
 * the user is on and, minimized, follows them around the app with everything
 * it holds. Nothing loads until a request is opened.
 */
export function EsignWorkspaceHost() {
  const session = useWorkspaceSession()
  if (!session) return null
  return (
    <>
      {session.minimized && <MinimizedWorkspaceCard session={session} />}
      {!session.dormant && (
        <Suspense fallback={null}>
          <EsignWorkspace key={session.envelopeId} session={session} />
        </Suspense>
      )}
    </>
  )
}
